/**
 * NotificationItem Component
 *
 * Single notification item with icon, title, body, and actions.
 */

import { Box, Typography, IconButton, Tooltip, alpha } from '@mui/material';
import {
  CheckCircle as ConfirmIcon,
  Schedule as ReminderIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  Error as ErrorIcon,
  LocalOffer as PromoIcon,
  Info as SystemIcon,
  Star as ReviewIcon,
  StorefrontOutlined as VendorIcon,
  Delete as DeleteIcon,
  Circle as UnreadDot,
  AccountBalance as PayoutIcon,
  CardMembership as SubscriptionIcon,
  Lock as LockIcon,
  Campaign as AnnouncementIcon,
  AdminPanelSettings as AdminIcon,
  CreditScore as CreditIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import type { Notification } from '../hooks/useNotifications';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClick?: (notification: Notification) => void;
}

const getNotificationIcon = (type: string) => {
  const iconMap: Record<string, React.ReactNode> = {
    BOOKING_CONFIRMATION: <ConfirmIcon sx={{ color: 'success.main' }} />,
    BOOKING_REMINDER: <ReminderIcon sx={{ color: 'warning.main' }} />,
    BOOKING_CANCELLED: <CancelIcon sx={{ color: 'error.main' }} />,
    PAYMENT_SUCCESS: <PaymentIcon sx={{ color: 'success.main' }} />,
    PAYMENT_FAILED: <ErrorIcon sx={{ color: 'error.main' }} />,
    REFUND_INITIATED: <PaymentIcon sx={{ color: 'info.main' }} />,
    REFUND_COMPLETED: <PaymentIcon sx={{ color: 'success.main' }} />,
    VENDOR_NEW_BOOKING: <VendorIcon sx={{ color: 'primary.main' }} />,
    VENDOR_APPROVAL: <ConfirmIcon sx={{ color: 'success.main' }} />,
    VENDOR_PENDING_CREDIT_ALERT: <CreditIcon sx={{ color: 'warning.main' }} />,
    VENDOR_DAILY_CREDIT_SUMMARY: <CreditIcon sx={{ color: 'info.main' }} />,
    PAYOUT_SUCCESS: <PayoutIcon sx={{ color: 'success.main' }} />,
    PAYOUT_FAILURE: <PayoutIcon sx={{ color: 'error.main' }} />,
    REVIEW_REQUEST: <ReviewIcon sx={{ color: 'warning.main' }} />,
    SUBSCRIPTION_ACTIVATED: <SubscriptionIcon sx={{ color: 'success.main' }} />,
    SUBSCRIPTION_TRIAL_REMINDER: <ReminderIcon sx={{ color: 'warning.main' }} />,
    SUBSCRIPTION_DOWNGRADED: <ErrorIcon sx={{ color: 'warning.main' }} />,
    SUBSCRIPTION_EXPIRED: <ErrorIcon sx={{ color: 'error.main' }} />,
    PASSWORD_CHANGED: <LockIcon sx={{ color: 'info.main' }} />,
    SYSTEM_ANNOUNCEMENT: <AnnouncementIcon sx={{ color: 'info.main' }} />,
    PROMOTION: <PromoIcon sx={{ color: 'secondary.main' }} />,
    SYSTEM: <SystemIcon sx={{ color: 'info.main' }} />,
    ADMIN_NEW_BOOKING: <AdminIcon sx={{ color: 'primary.main' }} />,
    ADMIN_BOOKING_CANCELLED: <AdminIcon sx={{ color: 'error.main' }} />,
    SESSION_EXPIRING: <ReminderIcon sx={{ color: 'warning.main' }} />,
  };

  return iconMap[type] || <SystemIcon sx={{ color: 'grey.500' }} />;
};

const getNotificationCta = (type: string): string | null => {
  const ctaMap: Record<string, string> = {
    BOOKING_CONFIRMATION: 'View Booking',
    BOOKING_REMINDER: 'View Booking',
    BOOKING_CANCELLED: 'View Booking',
    VENDOR_NEW_BOOKING: 'View Booking',
    PAYMENT_SUCCESS: 'View Booking',
    PAYMENT_FAILED: 'Retry Payment',
    REFUND_INITIATED: 'View Booking',
    REFUND_COMPLETED: 'View Booking',
    PAYOUT_SUCCESS: 'View Payouts',
    PAYOUT_FAILURE: 'View Payouts',
    VENDOR_APPROVAL: 'Go to Dashboard',
    REVIEW_REQUEST: 'Write Review',
    SUBSCRIPTION_ACTIVATED: 'Manage Subscription',
    SUBSCRIPTION_TRIAL_REMINDER: 'Manage Subscription',
    SUBSCRIPTION_DOWNGRADED: 'Manage Subscription',
    SUBSCRIPTION_EXPIRED: 'Renew Subscription',
    VENDOR_PENDING_CREDIT_ALERT: 'View Dashboard',
    VENDOR_DAILY_CREDIT_SUMMARY: 'View Dashboard',
    ADMIN_NEW_BOOKING: 'View Booking',
    ADMIN_BOOKING_CANCELLED: 'View Booking',
  };
  return ctaMap[type] ?? null;
};

export function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onClick,
}: NotificationItemProps) {
  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    onClick?.(notification);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });
  const ctaText = getNotificationCta(notification.type);

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        cursor: 'pointer',
        bgcolor: notification.isRead ? 'transparent' : (theme) => alpha(theme.palette.primary.main, 0.04),
        borderRadius: 1,
        transition: 'background-color 0.2s',
        '&:hover': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        },
        position: 'relative',
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'action.hover',
          flexShrink: 0,
        }}
      >
        {getNotificationIcon(notification.type)}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
          {!notification.isRead && (
            <UnreadDot sx={{ fontSize: 8, color: 'primary.main' }} />
          )}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: notification.isRead ? 400 : 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {notification.title}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 0.5,
          }}
        >
          {notification.body}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {timeAgo}
        </Typography>
        {ctaText && (
          <Typography
            variant="caption"
            sx={{ color: 'primary.main', fontWeight: 600, mt: 0.25, display: 'block' }}
          >
            {ctaText} →
          </Typography>
        )}
      </Box>

      {/* Delete button */}
      <Tooltip title="Delete">
        <IconButton
          size="small"
          onClick={handleDelete}
          sx={{
            opacity: 0,
            '.MuiBox-root:hover &': { opacity: 1 },
            transition: 'opacity 0.2s',
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
